import type { WorldRequest } from "./protocol.ts";

interface Job { id: number; resolve: (v: unknown) => void; reject: (e: Error) => void }

/** A fixed pool of world workers. Jobs carry an affinity key (region) so caches stay warm. */
export class WorkerPool {
  private workers: Worker[] = [];
  private pending: Map<number, Job>[] = [];
  private load: number[] = [];
  private nextId = 1;

  constructor(size = Math.max(2, Math.min(8, (navigator.hardwareConcurrency || 4) - 1))) {
    for (let i = 0; i < size; i++) {
      const w = new Worker(new URL("./world.worker.ts", import.meta.url), { type: "module" });
      const pend = new Map<number, Job>();
      w.onmessage = (e: MessageEvent<{ id: number; result?: unknown; error?: string }>) => {
        const j = pend.get(e.data.id);
        if (!j) return;
        pend.delete(e.data.id);
        this.load[i]--;
        if (e.data.error) j.reject(new Error(e.data.error));
        else j.resolve(e.data.result);
      };
      this.workers.push(w);
      this.pending.push(pend);
      this.load.push(0);
    }
  }

  get size() {
    return this.workers.length;
  }

  busy(i: number) {
    return this.load[i];
  }

  /** worker index for an affinity key */
  pick(key: number) {
    return ((key % this.size) + this.size) % this.size;
  }

  run<T>(worker: number, req: WorldRequest, transfer: Transferable[] = []): Promise<T> {
    const id = this.nextId++;
    this.load[worker]++;
    return new Promise<T>((resolve, reject) => {
      this.pending[worker].set(id, { id, resolve: resolve as (v: unknown) => void, reject });
      this.workers[worker].postMessage({ id, req }, transfer);
    });
  }

  broadcast(req: WorldRequest) {
    return Promise.all(this.workers.map((_, i) => this.run(i, req)));
  }

  dispose() {
    for (const w of this.workers) w.terminate();
    this.workers = [];
  }
}

export function regionKey(rx: number, rz: number) {
  return (Math.imul(rx, 73856093) ^ Math.imul(rz, 19349663)) >>> 0;
}
